import { TrendingUp } from "lucide-react";
import { useTrendingQuestions } from "@/hooks/useTrendingQuestions";

interface TrendingQuestionsProps {
  paused?: boolean;
  onPromptPick?: (prompt: string) => void;
}

/**
 * Google-style "Trending questions" leaderboard for the homepage.
 *
 * Mimics the mobile Google trending list (small "TrendingUp" arrow per row,
 * thin dividers, single-line truncated text) but rendered with our retro
 * warmth palette — peach background, ink-blue text, retro-amber arrow icon —
 * so it still reads as part of the AskThePods brand.
 *
 * Replaces the older single-question hero callout. Fed from search_logs over
 * the last 7 days. While loading, skeleton rows occupy the same vertical space
 * to avoid CLS during paint. If the query returns nothing (e.g. brand-new
 * deployment or analytics outage) the section quietly hides itself rather than
 * showing an empty leaderboard.
 */
export default function TrendingQuestions({ paused = false, onPromptPick }: TrendingQuestionsProps) {
  const { questions, loading } = useTrendingQuestions(7);

  // After load, if there's truly nothing to show, render nothing — no empty
  // header, no awkward placeholder.
  if (!loading && questions.length === 0) return null;

  return (
    <section
      aria-label="Trending questions"
      className={`w-full max-w-2xl mx-auto mt-6 sm:mt-8 px-4 transition-opacity duration-300 ${
        paused ? "opacity-30 pointer-events-none" : "opacity-100"
      }`}
    >
      <h2 className="font-mono text-[11px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 px-2">
        Trending questions
      </h2>

      <ul className="divide-y divide-border/70 border-t border-b border-border/70">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => (
              <li
                key={`tq-skeleton-${i}`}
                className="flex items-center gap-3 px-2 py-3"
                aria-hidden="true"
              >
                <div className="w-4 h-4 rounded bg-muted animate-pulse flex-shrink-0" />
                <div
                  className="h-3 rounded bg-muted animate-pulse"
                  style={{ width: `${55 + ((i * 11) % 35)}%` }}
                />
              </li>
            ))
          : questions.map((q) => (
              <li key={q.text}>
                <button
                  type="button"
                  onClick={() => onPromptPick?.(q.text)}
                  title={`Search: ${q.text}`}
                  className="w-full flex items-center gap-3 px-2 py-3 min-h-[44px] text-left font-mono text-sm text-ink-blue hover:bg-muted/60 active:bg-muted/60 transition-colors rounded-sm"
                >
                  <TrendingUp
                    className="w-4 h-4 flex-shrink-0 text-retro-amber"
                    aria-hidden="true"
                  />
                  <span className="flex-1 truncate">{q.text}</span>
                </button>
              </li>
            ))}
      </ul>
    </section>
  );
}
